import React, { useEffect, useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUnsavedWarning } from "../useUnsavedWarning";

// React Router
import { useNavigate, useParams } from "react-router-dom";

// i18n
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";

// MUI Core Components
import {
  Box,
  Paper,
  Grid,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Button,
  TextField,
  Divider,
  Typography,
  Container,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import FilePicker from "../../components/UI/FilePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FileIcon from "@mui/icons-material/FilePresent";

// MUI Icons
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

// Custom Components
import TreePicker from "../../components/UI/TreePicker";
import MUIAlert from "../../components/UI/MUIAlert";

// API
import {
  deleteAttachment,
  getAuctionById,
  updateAuction,
} from "../../api/auctions";

const createDefaultEndTime = () => {
  const date = new Date();
  date.setDate(date.getDate() + 20);
  date.setHours(10, 0, 0, 0);
  return date;
};

let propertyTypeLabels;

let regionLabels;

let forestStatusLabels;

let measurementStatusLabels;

let fellingTypeLabels;

let accessAgreementLabels;

let accessConditionsLabels;

let fellingOwnerLabels;

let contractTypeLabels;

let signatureTypeLabels;

let treeTypeLabels;

const twoWeeksFromNow = new Date();
twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

const EditAuction = () => {
  const { id } = useParams();
  const [auction, setAuction] = useState(null);
  const [error, setError] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false); // Added loading state
  const defaultEndTime = createDefaultEndTime();
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const [blockNavigation, setBlockNavigation] = React.useState(true);
  useUnsavedWarning(blockNavigation);

  propertyTypeLabels = {
    property: t("general.property"),
    felling: t("general.felling"),
    timber: t("general.timber"),
    wood_chips: t("general.wood_chips"),
  };

  regionLabels = {
    riga: t("forms.region_riga"),
    kurzeme: t("forms.region_kurzeme"),
    vidzeme: t("forms.region_vidzeme"),
    zemgale: t("forms.region_zemgale"),
    latgale: t("forms.region_latgale"),
  };

  forestStatusLabels = {
    none: t("forms.forest_status_none"),
    unknown: t("forms.forest_status_unknown"),
    partially: t("forms.forest_status_partially"),
    fully: t("forms.forest_status_fully"),
  };

  measurementStatusLabels = {
    no: t("forms.measurement_status_no"),
    yes: t("forms.measurement_status_yes"),
  };

  fellingTypeLabels = {
    clear: t("forms.felling_type_clear"),
    selection: t("forms.felling_type_selection"),
    maintenance: t("forms.felling_type_maintenance"),
    random: t("forms.felling_type_random"),
  };

  accessAgreementLabels = {
    none: t("forms.access_agreement_none"),
    agreed: t("forms.access_agreement_agreed"),
    verbal: t("forms.access_agreement_verbal"),
    not_agreed: t("forms.access_agreement_not_agreed"),
  };

  accessConditionsLabels = {
    none: t("forms.access_conditions_none"),
    bad: t("forms.access_conditions_bad"),
    okay: t("forms.access_conditions_okay"),
    good: t("forms.access_conditions_good"),
  };

  fellingOwnerLabels = {
    same: t("forms.felling_owner_same"),
    different: t("forms.felling_owner_different"),
  };

  contractTypeLabels = {
    standard: t("forms.contract_type_standard"),
    attachment: t("forms.contract_type_attachment"),
  };

  signatureTypeLabels = {
    digital: t("forms.signature_type_digital"),
    physical: t("forms.signature_type_physical"),
  };

  treeTypeLabels = [
    { label: t("forms.tree_type_unknown"), code: "unknown" },
    { label: t("forms.tree_type_priede"), code: "priede" },
    { label: t("forms.tree_type_egle"), code: "egle" },
    { label: t("forms.tree_type_berzs"), code: "berzs" },
    { label: t("forms.tree_type_apse"), code: "apse" },
    { label: t("forms.tree_type_malksnis"), code: "malksnis" },
    { label: t("forms.tree_type_balksnis"), code: "balksnis" },
    { label: t("forms.tree_type_ozols"), code: "ozols" },
    { label: t("forms.tree_type_osis"), code: "osis" },
    { label: t("forms.tree_type_liepa"), code: "liepa" },
    { label: t("forms.tree_type_bligzna"), code: "bligzna" },
    { label: t("forms.tree_type_klava"), code: "klava" },
    { label: t("forms.tree_type_sauskegle"), code: "sauskegle" },
    { label: t("forms.tree_type_sauskpriede"), code: "sauskpriede" },
    { label: t("forms.tree_type_other"), code: "other" },
  ];

  const step1Schema = yup.object().shape({
    property_type: yup
      .string()
      .required(t("errors.property_type_required"))
      .oneOf(
        ["property", "felling", "timber", "wood_chips"],
        t("errors.property_type_invalid")
      ),
    starting_price: yup
      .number()
      .typeError(t("errors.number_invalid"))
      .required(t("errors.start_price_required"))
      .positive(t("errors.number_positive")),
    end_time: yup
      .date()
      .required(t("errors.end_time_required"))
      .min(twoWeeksFromNow, t("errors.end_time_too_soon")),
  });

  const step2Schema = yup.object().shape({
    region: yup
      .string()
      .required(t("errors.region_required"))
      .oneOf(
        ["riga", "kurzeme", "vidzeme", "latgale", "zemgale"],
        t("errors.invalid")
      ),
    property_address: yup
      .string()
      .required(t("errors.property_address_required")),
    property_name: yup.string().required(t("errors.property_name_required")),
    cadastre: yup.string().required(t("errors.cadastre_required")),
  });

  const step3SchemaProperty = yup.object().shape({
    property_size: yup.string().required(t("errors.property_size_required")),
    forest_size: yup.string().required(t("errors.forest_size_required")),
    forest_status: yup.string().required(t("errors.forest_status_required")),
  });
  const step3SchemaFelling = yup.object().shape({
    harvestable_area: yup
      .string()
      .required(t("errors.harvestable_area_required")),
    liquid_felling_area: yup
      .string()
      .required(t("errors.liquid_felling_area_required")),
    measurement_status: yup
      .string()
      .required(t("errors.measurement_status_required")),
    felling_type: yup.string().required(t("errors.felling_type_required")),
    access_agreement: yup
      .string()
      .required(t("errors.access_agreement_required")),
    access_conditions: yup
      .string()
      .required(t("errors.access_conditions_required")),
    felling_owner: yup.string().required(t("errors.felling_owner_required")),
  });
  const step3SchemaTimber = yup.object().shape({
    timber_description: yup
      .string()
      .required(t("errors.timber_description_required")),
  });

  const step4Schema = yup.object().shape({
    contract_type: yup
      .string()
      .required(t("errors.contract_type_required"))
      .oneOf(["standard", "attachment"], t("errors.invalid")),
    signature_type: yup
      .string()
      .required(t("errors.signature_type_required"))
      .oneOf(["digital", "physical"], t("errors.invalid")),
  });

  const step5Schema = yup.object().shape({
    phone: yup
      .string()
      .transform((value) => value.replace(/\s+/g, ""))
      .matches(/^[0-9]{8,15}$/, t("errors.phone_invalid"))
      .required(t("errors.phone_required")),
    email: yup
      .string()
      .trim()
      .lowercase()
      .email(t("errors.email_invalid"))
      .required(t("errors.email_required")),
  });

  let auctionSchema = step1Schema
    .concat(step2Schema)
    .concat(step4Schema)
    .concat(step5Schema);

  const methods = useForm({
    resolver: yupResolver(auctionSchema),
    defaultValues: {
      endTime: "",
    },
  });
  const {
    register,
    handleSubmit,
    trigger,
    watch,
    reset,
    formState: { errors },
    setValue,
    control,
  } = methods;

  const values = watch();
  const trees = watch("trees");
  const property_type = watch("property_type");

  function EditableField({
    fieldName,
    label,
    value,
    multiline = false,
    options,
    datePicker = false,
  }) {
    const isEditing = editingField === fieldName;

    const {
      register,
      setValue,
      formState: { errors },
      trigger,
    } = methods;

    const [localValue, setLocalValue] = useState(value ?? "");
    const [originalValue, setOriginalValue] = useState(value ?? "");

    useEffect(() => {
      setLocalValue(value ?? "");
      if (isEditing) {
        setOriginalValue(value ?? "");
        setLocalValue(value ?? "");
      }
      register(fieldName, {
        required: `${label} ${t("edit_auction.is_required")}`,
      });
    }, [fieldName, label, register, value, isEditing]);

    const onSave = async () => {
      setValue(fieldName, localValue);
      const valid = await trigger(fieldName);
      if (valid) {
        setEditingField(null);
      }
    };

    const onCancel = () => {
      setLocalValue(originalValue);
      setEditingField(null);
    };

    const error = errors[fieldName];

    if (isEditing) {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {options ? (
            <Select
              size="small"
              fullWidth
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              sx={{ mr: 1, py: 0, minWidth: 120 }}
            >
              {Object.entries(options).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              size="small"
              multiline={multiline}
              fullWidth
              value={localValue}
              error={!!error}
              helperText={error?.message}
              onChange={(e) => setLocalValue(e.target.value)}
              sx={{ mr: 1, py: 0 }}
            />
          )}

          <IconButton
            size="small"
            color="success"
            onClick={onSave}
            aria-label={`${t("edit_auction.save")} ${label}`}
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={onCancel}
            aria-label={`${t("edit_auction.cancel")} ${label}`}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", ma: 0, py: 0 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1, my: 0, py: 0 }}>
          <b>{label}:</b> {options ? options[value] ?? value : value}
        </Typography>
        <IconButton
          sx={{ my: 0, py: 0 }}
          size="small"
          onClick={() => setEditingField(fieldName)}
          aria-label={`${t("edit_auction.edit")} ${label}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  const buildPayload = (values) => {
    const commonData = {
      startingPrice: values.starting_price,
      endTime: values.end_time ? values.end_time.toISOString() : null,
      propertyName: values.property_name,
      region: values.region,
      cadastre: values.cadastre,
      propertyAddress: values.property_address,
      coordinates: values.coordinates
        ? values.coordinates
        : { lat: 56.8796, lng: 24.6032 },
      contractType: values.contract_type,
      signatureType: values.signature_type,
      email: values.email,
      phone: values.phone,
      extraInfo: values.extra_info,
      attachments: existingAttachments,
      propertyId: propertyId,
      auctionId: Number(id),
    };

    switch (values.property_type) {
      case "property":
        return {
          ...commonData,
          propertyType: "property",
          propertySize: values.property_size,
          forestStatus: values.forest_status,
          forestSize: values.forest_size,
        };

      case "felling":
        return {
          ...commonData,
          propertyType: "felling",
          harvestableArea: values.harvestable_area,
          liquidFellingArea: values.liquid_felling_area,
          measurementStatus: values.measurement_status,
          fellingType: values.felling_type,
          accessAgreement: values.access_agreement,
          accessConditions: values.access_conditions,
          fellingOwner: values.felling_owner,
          treesOnProperty: trees,
        };

      case "timber":
        return {
          ...commonData,
          propertyType: "timber",
          timberDescription: values.timber_description,
        };

      case "wood_chips":
        return {
          ...commonData,
          propertyType: "wood_chips",
          timberDescription: values.timber_description,
        };

      default:
        return commonData; // fallback
    }
  };

  const verifyPayload = (payload) => {
    // Check if all required fields are filled
    const requiredFields = [
      "propertyType",
      "startingPrice",
      "endTime",
      "region",
      "propertyAddress",
      "contractType",
      "signatureType",
      "email",
      "phone",
    ];
    const requiredFieldsForProperty = [
      ...requiredFields,
      "propertyName",
      "cadastre",
      "propertySize",
      "forestSize",
      "forestStatus",
    ];
    const requiredFieldsForFelling = [
      ...requiredFields,
      "propertyName",
      "cadastre",
      "harvestableArea",
      "liquidFellingArea",
      "measurementStatus",
      "fellingType",
      "accessAgreement",
      "accessConditions",
      "fellingOwner",
      "treesOnProperty",
    ];

    let fieldsToCheck = [];

    // Decide based on propertyType
    if (payload.propertyType === "property") {
      fieldsToCheck = requiredFieldsForProperty;
    } else if (payload.propertyType === "felling") {
      fieldsToCheck = requiredFieldsForFelling;
    } else {
      fieldsToCheck = requiredFields;
    }

    const missingFields = fieldsToCheck.filter(
      (field) =>
        payload[field] === undefined ||
        payload[field] === null ||
        payload[field] === ""
    );

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = buildPayload(values);
      // console.log("Payload to be sent:", payload);
      const payloadStatus = verifyPayload(payload);
      if (!payloadStatus.isValid) {
        showAlert(t("errors.err"), "error");
        return;
      }

      let formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      attachments.forEach((file) => {
        formData.append("attachments", file); // name = "attachments"
      });

      const auction = await updateAuction(formData);
      if (auction) {
        setBlockNavigation(false);
        showAlert(t("edit_auction.update_success"));
        await new Promise((resolve) => setTimeout(resolve, 3000));
        navigate("/izsoles");
      }
    } catch (error) {
      setBlockNavigation(true);
      showAlert(t("edit_auction.update_failure"), "error");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const handleAttachmentDelete = async (file) => {
    try {
      const payload = { filePath: file.path, propertyId: propertyId };

      const auction = await deleteAttachment(JSON.stringify(payload));
      if (auction) {
        setExistingAttachments((prev) =>
          prev.filter((oldFile) => oldFile.name !== file.name)
        );
      }
    } catch (error) {
      showAlert(t("edit_auction.update_failure"), "error");
    }
  };

  useEffect(() => {
    const fetchAuction = async () => {
      try {
        const fetchedAuction = await getAuctionById(id);

        if (fetchedAuction._error) {
          navigate("/izsoles");
        }
        setAuction(fetchedAuction);

        if (fetchedAuction?.property_type == "property") {
          auctionSchema = auctionSchema.concat(step3SchemaProperty);
        }
        if (fetchedAuction?.property_type == "felling") {
          auctionSchema = auctionSchema.concat(step3SchemaFelling);
        }
        if (
          fetchedAuction?.property_type == "timber" ||
          fetchedAuction?.property_type == "wood_chips"
        ) {
          auctionSchema = auctionSchema.concat(step3SchemaTimber);
        }

        let treesData = JSON.parse(fetchedAuction?.trees_on_property || "[]");
        const formattedAuction = {
          ...fetchedAuction,
          email: fetchedAuction?.property_email,
          phone: fetchedAuction?.property_phone,
          end_time: fetchedAuction?.end_time
            ? new Date(fetchedAuction.end_time)
            : null,
          trees: treesData,
        };
        reset(formattedAuction);
        setValue("trees", treesData);
        setPropertyId(formattedAuction?.property_id);

        if (JSON.parse(fetchedAuction.attachments).length > 0) {
          const tmpFiles = [];
          JSON.parse(fetchedAuction.attachments).forEach((filePath) => {
            const fileName = filePath.split("/").pop(); // extract filename
            const ext = fileName.split(".").pop().toLowerCase();
            tmpFiles.push({ path: filePath, name: fileName });
          });
          setExistingAttachments(tmpFiles);
        }
      } catch (error) {
        console.error("Failed to fetch auctions:", error);
        setError(t("errors.err"));
      }
    };
    fetchAuction();
  }, [id, setValue]);

  const [editingField, setEditingField] = useState(null);

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "80vh", // 80vh height
          bgcolor: "background.paper",
          position: "relative",
          mt: 4,
          mb: 4,
          py: 4,
        }}
      >
        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }}
            sx={{ maxWidth: "600px" }}
          >
            <Button
              type="submit"
              variant="contained"
              fullWidth
              color="secondary"
              sx={{
                my: 2,
                py: 1,
                fontWeight: "bold",
                display: "flex", // Use flexbox for centering
                alignItems: "center",
                justifyContent: "center",
              }}
              disabled={loading} // Disable the button when loading
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" /> // Show spinner when loading
              ) : (
                t("edit_auction.save_changes")
              )}
            </Button>

            <Paper
              sx={{
                width: "100%",
                minWidth: "800px",
                maxWidth: "800px",
                p: 3,
                border: "1px solid lightgray",
                bgcolor: "#fcfcfc",
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", mb: 0, textAlign: "left" }}
              >
                {t("edit_auction.page_title")}
              </Typography>
              <Typography sx={{ mb: 2, textAlign: "left" }}>
                {t("edit_auction.page_subtitle")}
              </Typography>

              {errorMessage && (
                <Typography
                  variant="body2"
                  sx={{ color: "red", mb: 2, textAlign: "center" }}
                >
                  {errorMessage}
                </Typography>
              )}

              {(() => {
                const values = watch();

                return (
                  <>
                    {/* property_type never editable */}
                    <Typography variant="subtitle1">
                      <b>{t("forms.property_type")}:</b>{" "}
                      {propertyTypeLabels[values.property_type]}
                    </Typography>

                    <EditableField
                      fieldName="starting_price"
                      label={t("forms.start_price")}
                      value={values.starting_price}
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="end_time"
                        control={control}
                        rules={{ required: t("errors.end_time_required") }}
                        render={({ field }) => (
                          <DateTimePicker
                            {...field}
                            label={t("forms.end_time")}
                            onChange={(value) => field.onChange(value)}
                            value={field.value}
                            ampm={false} // 24h format
                            views={["year", "month", "day", "hours", "minutes"]}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                margin="normal"
                                size="small"
                                error={!!errors.end_time}
                                helperText={errors.end_time?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <Divider sx={{ my: 2 }} />

                    <EditableField
                      fieldName="property_name"
                      label={t("forms.property_name")}
                      value={values.property_name}
                    />

                    <EditableField
                      fieldName="region"
                      label={t("forms.region")}
                      value={values.region}
                      options={regionLabels}
                    />

                    <EditableField
                      fieldName="cadastre"
                      label={t("forms.cadastre")}
                      value={values.cadastre}
                    />

                    <EditableField
                      fieldName="property_address"
                      label={t("forms.property_address")}
                      value={values.property_address}
                    />

                    <Divider sx={{ my: 2 }} />

                    {values.property_type === "property" && (
                      <>
                        {values.status === "inactive" ? (
                          <EditableField
                            fieldName="property_size"
                            label={t("forms.property_size")}
                            value={values.property_size}
                            suffix="ha"
                          />
                        ) : (
                          <Typography variant="subtitle1">
                            <b>{t("forms.property_size")}:</b>{" "}
                            {values.property_size} ha
                          </Typography>
                        )}

                        <EditableField
                          fieldName="forest_status"
                          label={t("forms.forest_status")}
                          value={values.forest_status}
                          options={forestStatusLabels}
                        />

                        {values.forest_status !== "none" && (
                          <EditableField
                            fieldName="forest_size"
                            label={t("forms.forest_size")}
                            value={values.forest_size}
                            suffix="ha"
                          />
                        )}
                      </>
                    )}

                    {values.property_type === "felling" && (
                      <>
                        {values.status === "inactive" ? (
                          <EditableField
                            fieldName="harvestable_area"
                            label={t("forms.harvestable_area")}
                            value={values.harvestable_area}
                            suffix="ha"
                          />
                        ) : (
                          <Typography variant="subtitle1">
                            <b>{t("forms.harvestable_area")}:</b>{" "}
                            {values.harvestable_area} ha
                          </Typography>
                        )}

                        <>
                          <EditableField
                            fieldName="liquid_felling_area"
                            label={t("forms.liquid_felling_area")}
                            value={values.liquid_felling_area}
                            suffix="ha"
                          />

                          <EditableField
                            fieldName="measurement_status"
                            label={t("forms.measurement_status")}
                            value={values.measurement_status}
                            options={measurementStatusLabels}
                          />

                          <EditableField
                            fieldName="felling_type"
                            label={t("forms.felling_type")}
                            value={values.felling_type}
                            options={fellingTypeLabels}
                          />

                          <EditableField
                            fieldName="access_agreement"
                            label={t("forms.access_agreement")}
                            value={values.access_agreement}
                            options={accessAgreementLabels}
                          />

                          <EditableField
                            fieldName="access_conditions"
                            label={t("forms.access_conditions")}
                            value={values.access_conditions}
                            options={accessConditionsLabels}
                          />

                          <EditableField
                            fieldName="felling_owner"
                            label={t("forms.felling_owner")}
                            value={values.felling_owner}
                            options={fellingOwnerLabels}
                          />

                          <Divider sx={{ my: 2 }} />

                          <Typography variant="subtitle1" sx={{ my: 2 }}>
                            <b>{t("forms.tree_types")}:</b>
                          </Typography>
                          <TreePicker
                            value={trees || []}
                            onChange={(newTrees) => setValue("trees", newTrees)}
                          />
                        </>
                      </>
                    )}

                    {(values.property_type === "timber" ||
                      values.property_type === "wood_chips") && (
                      <Typography variant="subtitle1">
                        <b>{t("forms.timber_offer")}:</b>{" "}
                        {values.timber_description}
                      </Typography>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <EditableField
                      fieldName="contract_type"
                      label={t("forms.contract_type")}
                      value={values.contract_type}
                      options={contractTypeLabels}
                    />
                    <EditableField
                      fieldName="signature_type"
                      label={t("forms.signature_type")}
                      value={values.signature_type}
                      options={signatureTypeLabels}
                    />
                    <Divider sx={{ my: 2 }} />
                    <EditableField
                      fieldName="email"
                      label={t("forms.extra_email")}
                      value={values.email}
                    />
                    <EditableField
                      fieldName="phone"
                      label={t("forms.extra_phone")}
                      value={values.phone}
                    />
                    <EditableField
                      fieldName="extra_info"
                      label={t("forms.extra_info")}
                      value={values.extra_info}
                    />
                  </>
                );
              })()}
            </Paper>

            <Paper
              sx={{
                width: "100%",
                minWidth: "800px",
                maxWidth: "800px",
                p: 3,
                mt: 3,
                border: "1px solid lightgray",
                bgcolor: "#fcfcfc",
              }}
            >
              <Typography
                variant="h5"
                sx={{ pb: 2, fontWeight: "bold", mb: 0, textAlign: "left" }}
              >
                {t("edit_auction.files_section_title1")}
              </Typography>
              <FilePicker onFilesChange={setAttachments} />
            </Paper>
            <Paper
              sx={{
                width: "100%",
                minWidth: "800px",
                maxWidth: "800px",
                p: 3,
                mt: 3,
                border: "1px solid lightgray",
                bgcolor: "#fcfcfc",
              }}
            >
              <Typography
                variant="h5"
                sx={{ pb: 2, fontWeight: "bold", mb: 0, textAlign: "left" }}
              >
                {t("edit_auction.files_section_title2")}
              </Typography>
              <List sx={{ width: "100%", my: 2 }}>
                {existingAttachments?.map((file, index) => {
                  const isImage = file.path.match(
                    /\.(jpeg|jpg|png|gif|webp)$/i
                  );

                  return (
                    <div key={index}>
                      <ListItem
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleAttachmentDelete(file)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        {isImage && (
                          <ListItemAvatar sx={{ p: 1 }}>
                            <img
                              src={file.path}
                              alt={file.name}
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          </ListItemAvatar>
                        )}
                        {!isImage && (
                          <ListItemAvatar sx={{ p: 1 }}>
                            <FileIcon sx={{ fontSize: "3rem" }} />
                          </ListItemAvatar>
                        )}
                        <ListItemText
                          primary={file.name}
                          secondary={
                            <a
                              href={file.path}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.path}
                            </a>
                          }
                        />
                      </ListItem>
                      {index < existingAttachments.length - 1 && <Divider />}
                    </div>
                  );
                })}
              </List>
            </Paper>
          </form>
        </FormProvider>
      </Container>
    </>
  );
};

export default EditAuction;
